import { NotificationController } from 'core-components/library/popup'
import { getAppId, getAppOwnerId } from 'src/core/classes/helpers/getAppId'
import {
  clear,
  getIdentityTokens,
  getTokens,
  setIdentityTokens,
  setTokens,
} from 'src/core/classes/helpers/StorageHelper'
import { proxy, proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'

import { localStorageLocker } from './LocalStorageLocker'

enum ETokenMessageType {
  RefreshStarted = 'refresh_started',
  RefreshDone = 'refresh_done',
  RefreshFailed = 'refresh_failed',
}

type TokenMessage =
  | { type: ETokenMessageType.RefreshStarted }
  | { type: ETokenMessageType.RefreshDone; accessToken: string; refreshToken: string }
  | { type: ETokenMessageType.RefreshFailed }

export class TokenRefresher {
  private tokenChannel = window.BroadcastChannel
    ? new BroadcastChannel('token_refresh_channel')
    : null

  constructor() {
    if (this.tokenChannel) {
      this.tokenChannel.onmessage = this.handleChannelMessage
    }
  }

  private handleChannelMessage = (event: MessageEvent<TokenMessage>) => {
    const message = event.data

    switch (message.type) {
      case ETokenMessageType.RefreshStarted:
        break

      case ETokenMessageType.RefreshDone:
      case ETokenMessageType.RefreshFailed:
        this.finalizeRefresh()
        break
    }
  }

  private finalizeRefresh = () => {
    localStorageLocker.clearLock()
  }

  public isRefreshing = () => {
    return localStorageLocker.isLocked()
  }

  public refreshToken = async (isIdentity: boolean = false): Promise<string | null> => {
    if (this.isRefreshing()) {
      // A different browser tab started the token refresh
      return null
    }

    try {
      localStorageLocker.setLock()
      this.tokenChannel?.postMessage({ type: ETokenMessageType.RefreshStarted })

      const [accessToken, refreshToken] = isIdentity ? getIdentityTokens() : getTokens()

      if (!accessToken && !refreshToken) {
        return null
      }

      const url = isIdentity
        ? proxyIdentity('/mbe/api/v2/access/token')
        : proxy('/mbe/api/v2/access/token')
      const payload = {
        AppId: getAppId(),
        AccessToken: accessToken,
        RefreshToken: refreshToken,
      }

      const response = await Rest.put({ url, payload, force: true, identityToken: isIdentity })

      if (response?.AccessToken && response?.RefreshToken) {
        if (isIdentity) {
          setIdentityTokens(response.AccessToken, response.RefreshToken)
        } else {
          setTokens(getAppOwnerId(), response.AccessToken, response.RefreshToken)
        }

        this.tokenChannel?.postMessage({
          type: ETokenMessageType.RefreshDone,
          accessToken: response.AccessToken,
          refreshToken: response.RefreshToken,
        })

        return `${response.AccessToken}`
      }

      throw new Error('Empty response')
    } catch (err) {
      NotificationController.showError({ message: 'Token refresh error' })
      clear()

      this.tokenChannel?.postMessage({ type: ETokenMessageType.RefreshFailed })

      window.location.reload()
      return null
    } finally {
      this.finalizeRefresh()
    }
  }
}
